import { Component, OnInit, Input, Output, EventEmitter, ViewChild, HostListener } from '@angular/core';
import { Observable } from 'rxjs';
import { CountryModel } from 'src/app/shared/models/CountryModel';
import { startWith, map, count, filter } from 'rxjs/operators';
import { UntypedFormGroup, ControlContainer, FormGroupDirective } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'terra-country-auto-complete',
  templateUrl: './country-auto-complete.component.html',
  styleUrls: ['./country-auto-complete.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class CountryAutoCompleteComponent implements OnInit {

  @Input()
  formGroupRef: UntypedFormGroup;

  @Input()
  formGroupNameValue: string;

  @Input()
  controlName: string;

  @Input()
  placeholderText: string;

  @Input()
  countriesList: CountryModel[];

  @Output()
  changedCountry = new EventEmitter<CountryModel>();

  @Input()
  inputId = '';

  @ViewChild('countryInput', { read: MatAutocompleteTrigger }) triggerAutocompleteInput: MatAutocompleteTrigger;

  filteredCountries: Observable<CountryModel[]>;
  selectedCountryId: number = 0;
  internalFilteredCountries: CountryModel[];
  form: UntypedFormGroup;
  isAutoFilled: boolean = false;

  constructor(private parentForm: FormGroupDirective) { }

  ngOnInit() {
    this.form = this.formGroupNameValue ? this.parentForm.form.get(this.formGroupNameValue) as UntypedFormGroup : this.parentForm.form;

    const formControlValue = this.form.get(this.controlName)?.value;
    this.selectedCountryId = formControlValue ? Number(formControlValue) : null;
    this.filteredCountries = this.form.get(this.controlName).valueChanges
      .pipe(
        startWith(this.selectedCountryId ? this.selectedCountryId : ''),
        map(filter => this.filterCountries(filter))
      );
  }

  filterCountries(filter: string): CountryModel[] {

    this.updateOptionsPosition();
    if (Number(filter) == 0) {
      filter = '';
    }

    if (typeof filter === 'number') {
      // This means a country was selected from the autocomplete list or was fully typed, no need for filtering
      this.internalFilteredCountries = this.countriesList.filter(x => x.id == Number(filter));
      this.checkForCountryChange(Number(filter));
      return this.internalFilteredCountries;
    } else {
      // Filter the countries list for countries that start with the filter value.
      const filterValue = filter ? filter.trim().toLowerCase() : '';
      if (filterValue == '') {
        // Skip filtering if empty filter
        this.internalFilteredCountries = this.countriesList;
        this.checkForCountryChange(0);
        return this.internalFilteredCountries;
      }
      const filteredCountries = this.countriesList.filter(country => country.name.toLowerCase().startsWith(filterValue));

      // If the filter matches a single country, it means the full country name was inputted - select that country.
      if (filteredCountries.length == 1 && filteredCountries[0].name.toLowerCase() == filterValue) {
        this.form.get(this.controlName).setValue(filteredCountries[0].id, {emitModelToViewChange: false});
      } else {
        // If the filter was autofilled, clear it
        if (this.isAutoFilled) {
          this.form.get(this.controlName).reset();
        } else {
          this.internalFilteredCountries = filteredCountries;
          this.checkForCountryChange(0);
        }
      }
      return this.internalFilteredCountries;
    }
  }

  displayFn(value?: number): string | undefined {
    // Get the view value from the options
    if (value) {
      const option = this['options']['_results'].find(x => x.value == value);
      return option ? option.viewValue : undefined;
    } else {
      return undefined;
    }
  }

  conutryTrackByFn(index, item: CountryModel) {
    return item.id; // or index
  }

  checkForCountryChange(countryId: number) {
    if (this.selectedCountryId != countryId) {
      this.selectedCountryId = countryId;
      if (this.selectedCountryId != 0) {
        this.changedCountry.emit(this.countriesList.find(x => x.id == this.selectedCountryId));
      } else {
        this.changedCountry.emit(null);
      }
    }
  }

  setAutofillState(state: boolean) {
    this.isAutoFilled = state;
  }

  isError() {
    return this.form.get(this.controlName).hasError('required') &&
           (this.form.get(this.controlName).dirty || this.form.get(this.controlName).touched);
  }

  clearText(){
    this.form.get(this.controlName).setValue('');
    this.form.get(this.controlName).updateValueAndValidity();
  }

  // Update the autocomplete overlay position after DOM changes (animations, ngifs, etc) triggered by value changes / form validation errors.
  // (@guy: This is a very ugly hack and I hate myself for using it...)
  updateOptionsPosition() {
    setTimeout(() => {
      this.triggerAutocompleteInput.updatePosition();
    }, 100);
    setTimeout(() => {
      this.triggerAutocompleteInput.updatePosition();
    }, 300);
    setTimeout(() => {
      this.triggerAutocompleteInput.updatePosition();
    }, 500);
  }
}
